// src/services/farmPulseService.js

/**
 * FarmPulse Service Layer
 * ------------------------------------------------------------------
 * Handles all Firestore reads/writes, data aggregation, validation,
 * contributor management, and PDF export.
 */

import {
  collection,
  query,
  orderBy,
  getDocs,
  onSnapshot,
  addDoc,
  doc,
  getDoc,
  where,
  serverTimestamp,
  updateDoc,
  increment,
  limit as fbLimit,
  runTransaction,
} from "../lib/firestoreClient";
import { db } from "../lib/firebase";
import {
  COLLECTIONS,
  CACHE_KEYS,
  SEASONS,
  VALIDATION_THRESHOLD,
  VALIDATION_MARGIN_PCT,
  IP_PER_PRICE_SUBMISSION,
  IP_PER_PLANTING_SUBMISSION,
  HIGHLIGHT_LIMIT,
  SUPPLY_ALERT_LIMIT,
} from "../constants/farmPulseConstants";
import {
  linearRegression,
  classifySignal,
  pctChange,
  buildSparkline,
  generatePredictions,
  toDailySeries,
} from "../utils/farmPulsePredictor";
import { canUsePreferencesStorage } from "../utils/storageConsent";
import { errorDev } from '../utils/logger.js';

// ─── Date helpers ──────────────────────────────────────────────────
const DAY_MS = 86_400_000;
const daysAgo = (n) => new Date(Date.now() - n * DAY_MS);
const now = () => new Date();
const round = (v, d = 2) =>
  typeof v === "number" && isFinite(v) ? +v.toFixed(d) : v;

export const PRICE_DIRECTION = {
  rising: { arrow: "▲", color: "#16a34a" },
  falling: { arrow: "▼", color: "#dc2626" },
  stable: { arrow: "■", color: "#6b7280" },
};

function toDate(ts) {
  if (!ts) return null;
  if (typeof ts?.toDate === "function") return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

function safeNumber(x) {
  if (typeof x === "number" && isFinite(x)) return x;
  if (typeof x === "string") {
    const n = parseFloat(x.replace(/[^0-9.\-]/g, ""));
    return isNaN(n) ? null : n;
  }
  return null;
}

function extractPricePerUnit(product) {
  const unit = product?.unitType || product?.quantityUnit || "kg";
  const direct = safeNumber(product?.price);
  if (direct != null && unit !== "kg") return direct;
  const perKg = safeNumber(product?.pricePerKg);
  if (perKg != null) return perKg;
  return direct;
}

function extractUnit(product) {
  return product?.unitType || product?.quantityUnit || product?.unit || null;
}

function monthInSeason(date, seasonKey) {
  if (seasonKey === "ALL") return true;
  const m = (date instanceof Date ? date : new Date(date)).getMonth();
  const entry = SEASONS.find((s) => s.key === seasonKey);
  return entry ? entry.months.includes(m) : true;
}

function groupBy(arr, fn) {
  return arr.reduce((acc, item) => {
    const k = fn(item);
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {});
}

// ─── Fetch products (one-shot, for offline fallback) ──────────────
export async function fetchProductsOnce() {
  const colRef = collection(db, COLLECTIONS.PRODUCTS);
  const qy = query(colRef, orderBy("createdAt", "desc"));
  const snap = await getDocs(qy);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  cacheProducts(rows);
  return rows;
}

// ─── Realtime products listener ──────────────────────────────────
export function subscribeProducts(onData, onError) {
  const colRef = collection(db, COLLECTIONS.PRODUCTS);
  const qy = query(colRef, orderBy("createdAt", "desc"));
  return onSnapshot(
    qy,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cacheProducts(rows);
      onData(rows);
    },
    (err) => {
      if (onError) onError(err);
    }
  );
}

// ─── Cache helpers ───────────────────────────────────────────────
function cacheProducts(products) {
  try {
    if (!canUsePreferencesStorage()) return;
    localStorage.setItem(CACHE_KEYS.PRODUCTS, JSON.stringify(products));
    localStorage.setItem(CACHE_KEYS.PRODUCTS_AT, String(Date.now()));
  } catch (err) {
    // Storage full or unavailable — silently ignore
  errorDev("Operation failed:", err);
  }
}

export function getCachedProducts() {
  try {
    if (!canUsePreferencesStorage()) return [];
    const cached = JSON.parse(localStorage.getItem(CACHE_KEYS.PRODUCTS) || "null");
    return Array.isArray(cached) ? cached : [];
  } catch (err) {
    errorDev("Operation failed:", err);
    return [];
  }
}

export function getCacheTime() {
  if (!canUsePreferencesStorage()) return null;
  const raw = localStorage.getItem(CACHE_KEYS.PRODUCTS_AT);
  return raw ? parseInt(raw, 10) : null;
}

// ─── Extract districts ──────────────────────────────────────────
export function extractDistricts(products) {
  const set = new Set();
  for (const p of products) {
    const d = p?.location?.district;
    if (d) set.add(d);
  }
  return Array.from(set).sort();
}

// ─── Filter products ────────────────────────────────────────────
export function filterProducts(products, {
  search,
  district,
  season,
  windowDays,
  category,
  crop,
  dateFrom,
  dateTo,
} = {}) {
  const s = (search || "").trim().toLowerCase();
  const windowStart = dateFrom ? new Date(dateFrom) : daysAgo(windowDays || 90);
  const windowEnd = dateTo ? new Date(dateTo) : now();

  return products.filter((p) => {
    const price = extractPricePerUnit(p);
    if (price == null) return false;
    const created = toDate(p.createdAt) || now();
    if (created < windowStart || created > windowEnd) return false;
    if (district && district !== "ALL" && (p?.location?.district || "") !== district) return false;
    if (!monthInSeason(created, season)) return false;
    if (category && category !== "ALL" && (p.category || "") !== category) return false;
    if (crop && crop !== "ALL" && (p.title || p.name || "") !== crop) return false;
    if (s) {
      const hay = [
        p.title, p.name, p.category, p.unit,
        p?.location?.province, p?.location?.district,
        p?.location?.sector, p?.location?.cell,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });
}

// ─── Aggregate per crop ─────────────────────────────────────────
export function aggregateByCrop(filtered, season) {
  // Group by individual crop name (title/name), NOT by category
  const by = groupBy(filtered, (p) => {
    const crop = p.title || p.name || p.category || "Other";
    return `${crop}::${extractUnit(p) || "unknown"}`;
  });

  const rows = Object.entries(by)
    .map(([, items]) => {
      const crop = items[0]?.title || items[0]?.name || items[0]?.category || "Other";
      // Resolve category from the first item that has one
      const category = items.find((i) => i.category)?.category || "";

      const pts = items
        .map((x) => ({ t: toDate(x.createdAt) || now(),         price: extractPricePerUnit(x), item: x, unit: extractUnit(x) }))
        .filter((x) => x.price != null)
        .sort((a, b) => a.t - b.t);

      if (pts.length === 0) return null;

      const lastPoint = pts[pts.length - 1];
      const last = lastPoint.price;
      const first = pts[0].price;

      const idx30 = pts.findIndex((p) => p.t >= daysAgo(30));
      const base30 = idx30 >= 0 ? pts[idx30].price : first;
      const idx7 = pts.findIndex((p) => p.t >= daysAgo(7));
      const base7 = idx7 >= 0 ? pts[idx7].price : base30;

      // Previous 7d base (7-14 days ago)
      const idx14 = pts.findIndex((p) => p.t >= daysAgo(14));
      const base14 = idx14 >= 0 ? pts[idx14].price : first;

      const change7 = pctChange(base7, last);
      const change30 = pctChange(base30, last);
      const change90 = pctChange(first, last);
      const changePrev7 = pctChange(base14, base7);
      const change30Abs = base30 != null && last != null ? round(last - base30) : null;

      // Price status: compare 7d vs previous 7d
      let priceStatus = "stable";
      if (change7 != null && changePrev7 != null) {
        const diff = (change7 || 0) - (changePrev7 || 0);
        if (diff > 3) priceStatus = "rising";
        else if (diff < -3) priceStatus = "falling";
      }

      const vol7 = items.filter((i) => (toDate(i.createdAt) || now()) >= daysAgo(7)).length;
      const vol30 = items.filter((i) => (toDate(i.createdAt) || now()) >= daysAgo(30)).length;
      const volPrev30 = items.filter((i) => {
        const d = toDate(i.createdAt) || now();
        return d < daysAgo(30) && d >= daysAgo(60);
      }).length;
      const volumeTrend30 = pctChange(volPrev30, vol30);

      const seasonEntry = SEASONS.find((se) => se.key === season);
      const seasonAdj = seasonEntry ? 0 : 0; // Season adjustment is handled in predictor

      const dailyPts = toDailySeries(pts);
      const reg = linearRegression(dailyPts.length >= 2 ? dailyPts : pts);
      let proj30 = null;
      let proj60 = null;
      let proj30Pct = null;
      if (reg) {
        const tLast = (dailyPts[dailyPts.length - 1] || pts[pts.length - 1]).t.getTime();
        proj60 = reg.intercept + reg.slope * (tLast + 60 * DAY_MS);
      }

      const spark = buildSparkline(pts);
      const sig = classifySignal({
        trend30: change30,
        trend90: change90,
        volumeTrend30,
        seasonAdj,
        proj30Pct,
      });

      const low = Math.min(...pts.map((p) => p.price));
      const high = Math.max(...pts.map((p) => p.price));

      // Generate predictions
      const predictions = generatePredictions(pts, crop);

      proj30 = predictions?.["1M"]?.predicted ?? null;
      if (proj30 != null && isFinite(last) && last !== 0) {
        proj30Pct = ((proj30 - last) / Math.abs(last)) * 100;
      }

      // Extract prediction values for 3M and 6M forecast columns
      const proj90 = predictions?.["3M"]?.predicted ?? null;
      const proj180 = predictions?.["6M"]?.predicted ?? null;

      return {
        cropKey: `${crop}::${lastPoint.unit || "unknown"}`,
        crop,
        category,
        quantity: safeNumber(lastPoint.item?.quantity),
        unit: lastPoint.unit,
        priceUnit: lastPoint.unit,
        last: round(last),
        change7: change7 != null ? round(change7, 1) : null,
        change30: change30 != null ? round(change30, 1) : null,
        change30Abs,
        change90: change90 != null ? round(change90, 1) : null,
        priceStatus,
        volume: items.length,
        vol7,
        vol30,
        volumeTrend30: volumeTrend30 != null ? round(volumeTrend30, 1) : null,
        low: round(low),
        high: round(high),
        spark,
        signal: sig.label,
        score: sig.score,
        proj30: proj30 != null ? round(proj30) : null,
        proj60: proj60 != null ? round(proj60) : null,
        proj90: proj90 != null ? round(proj90) : null,
        proj180: proj180 != null ? round(proj180) : null,
        proj30Pct: proj30Pct != null ? round(proj30Pct, 1) : null,
        lastDate: pts[pts.length - 1].t,
        predictions,
        dataPoints: pts.length,
      };
    })
    .filter(Boolean);

  return rows;
}

// ─── Sorting ────────────────────────────────────────────────────
export function sortCropData(rows, sortBy) {
  const sorted = [...rows];
  switch (sortBy) {
    case "gainers":
      sorted.sort((a, b) => (b.change30 ?? -999) - (a.change30 ?? -999));
      break;
    case "losers":
      sorted.sort((a, b) => (a.change30 ?? 999) - (b.change30 ?? 999));
      break;
    case "volume":
      sorted.sort((a, b) => (b.vol30 ?? 0) - (a.vol30 ?? 0));
      break;
    case "price":
      sorted.sort((a, b) => (b.last ?? 0) - (a.last ?? 0));
      break;
    default:
      break;
  }
  return sorted;
}

// ─── Highlight slices ───────────────────────────────────────────
export function getHighlights(byCrop) {
  const topGainers = [...byCrop]
    .sort((a, b) => (b.change30 ?? -999) - (a.change30 ?? -999))
    .slice(0, HIGHLIGHT_LIMIT);
  const topLosers = [...byCrop]
    .sort((a, b) => (a.change30 ?? 999) - (b.change30 ?? 999))
    .slice(0, HIGHLIGHT_LIMIT);
  const oversupply = [...byCrop]
    .sort((a, b) => (b.volumeTrend30 ?? -999) - (a.volumeTrend30 ?? -999))
    .slice(0, SUPPLY_ALERT_LIMIT);
  const undersupply = [...byCrop]
    .sort((a, b) => (a.volumeTrend30 ?? 999) - (b.volumeTrend30 ?? 999))
    .slice(0, SUPPLY_ALERT_LIMIT);

  return { topGainers, topLosers, oversupply, undersupply };
}

// ─── Price submission ───────────────────────────────────────────
export async function submitPrice({ userId, userName, category, crop, price, unit, district }) {
  const docRef = await addDoc(collection(db, COLLECTIONS.PRICE_SUBMISSIONS), {
    userId,
    userName,
    category,
    crop,
    price: Number(price),
    unit,
    district: district || null,
    createdAt: serverTimestamp(),
    validated: false,
    pointsAwarded: false,
  });

  await tryValidatePriceReportForSubmitter({
    submissionId: docRef.id,
    userId,
    crop,
  });

  // Best-effort: log to impactFeed
  try {
    await addDoc(collection(db, COLLECTIONS.IMPACT_FEED), {
      user: userId,
      userName,
      type: "price_contribution",
      timestamp: serverTimestamp(),
      impactData: { crop, price: Number(price), unit, category, district: district || null },
    });
  } catch (err) { errorDev("Operation failed:", err);  /* non-critical */ }

  return docRef.id;
}

function getValidatedCluster(submissions) {
  const usable = submissions.filter(
    (s) => Number.isFinite(Number(s?.price)) && s?.userId
  );
  if (usable.length < VALIDATION_THRESHOLD) return [];

  const prices = usable.map((s) => Number(s.price)).sort((a, b) => a - b);
  const median = prices[Math.floor(prices.length / 2)] || 0;
  const margin = Math.abs(median) * (VALIDATION_MARGIN_PCT / 100);

  const cluster = usable.filter((s) => Math.abs(Number(s.price) - median) <= margin);
  const uniqueUsers = new Set(cluster.map((s) => String(s.userId))).size;
  return uniqueUsers >= VALIDATION_THRESHOLD ? cluster : [];
}

async function awardValidatedPriceSubmission(submission) {
  if (!submission?.id || !submission?.userId) return false;

  const submissionRef = doc(db, COLLECTIONS.PRICE_SUBMISSIONS, submission.id);
  const userRef = doc(db, COLLECTIONS.USERS, String(submission.userId));
  const eventRef = doc(collection(db, "impact_points_events"));

  return runTransaction(db, async (tx) => {
    const subSnap = await tx.get(submissionRef);
    if (!subSnap.exists()) return false;

    const current = subSnap.data() || {};
    if (current.pointsAwarded) {
      tx.set(submissionRef, { validated: true, validatedAt: serverTimestamp() }, { merge: true });
      return false;
    }

    tx.set(submissionRef, {
      validated: true,
      validatedAt: serverTimestamp(),
      pointsAwarded: true,
      pointsAwardedAt: serverTimestamp(),
      pointsAwardedValue: IP_PER_PRICE_SUBMISSION,
    }, { merge: true });

    tx.set(userRef, {
      points: increment(IP_PER_PRICE_SUBMISSION),
      updatedAt: serverTimestamp(),
      "impactBreakdown.priceValidatedPoints": increment(IP_PER_PRICE_SUBMISSION),
      "impactBreakdown.categories.priceScouts": increment(IP_PER_PRICE_SUBMISSION),
    }, { merge: true });

    tx.set(eventRef, {
      userId: String(submission.userId),
      action: "price_report_validated",
      category: "priceScouts",
      points: IP_PER_PRICE_SUBMISSION,
      metadata: {
        crop: current.crop || submission.crop || null,
        district: current.district || submission.district || null,
        submissionId: submission.id,
      },
      createdAt: serverTimestamp(),
    });

    return true;
  });
}

async function tryValidatePriceReportForSubmitter({ submissionId, userId, crop }) {
  if (!crop || !submissionId || !userId) return;

  const submissions = await getCropSubmissions(crop, 7);
  const cluster = getValidatedCluster(submissions);
  if (cluster.length === 0) return;

  const target = cluster.find(
    (submission) => submission.id === submissionId && String(submission.userId) === String(userId)
  );
  if (!target) return;

  await awardValidatedPriceSubmission(target);
}

// ─── Planting intention submission ──────────────────────────────
export async function submitPlantingIntention({
  userId,
  userName,
  category,
  crop,
  status,
  plantDate,
  areaHectares,
  district,
}) {
  const docRef = await addDoc(collection(db, COLLECTIONS.PLANTING_INTENTIONS), {
    userId,
    userName,
    category,
    crop,
    status, // "planted" or "plan_to_plant"
    plantDate,
    areaHectares: Number(areaHectares) || 0,
    district: district || null,
    createdAt: serverTimestamp(),
  });

  // Best-effort: award IP
  if (IP_PER_PLANTING_SUBMISSION > 0) {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, { points: increment(IP_PER_PLANTING_SUBMISSION) });
    } catch (err) { errorDev("Operation failed:", err);  /* non-critical */ }
  }

  // Best-effort: log to impactFeed
  try {
    await addDoc(collection(db, COLLECTIONS.IMPACT_FEED), {
      user: userId,
      userName,
      type: "planting_intention",
      timestamp: serverTimestamp(),
      impactData: { crop, status, areaHectares: Number(areaHectares) || 0, category },
    });
  } catch (err) { errorDev("Operation failed:", err);  /* non-critical */ }

  return docRef.id;
}

// ─── Check if user already submitted today for a crop ───────────
export async function hasSubmittedToday(userId, crop) {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const qy = query(
      collection(db, COLLECTIONS.PRICE_SUBMISSIONS),
      where("userId", "==", userId),
      where("crop", "==", crop),
      where("createdAt", ">=", today),
      fbLimit(1)
    );
    const snap = await getDocs(qy);
    return !snap.empty;
  } catch (err) {
    errorDev("Operation failed:", err);
    return false;
  }
}

// ─── Get price submissions for a crop (for validation) ──────────
export async function getCropSubmissions(crop, daysBack = 7) {
  const since = daysAgo(daysBack);
  const qy = query(
    collection(db, COLLECTIONS.PRICE_SUBMISSIONS),
    where("crop", "==", crop),
    where("createdAt", ">=", since),
    orderBy("createdAt", "desc")
  );
  const snap = await getDocs(qy);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── Get user's contribution stats ──────────────────────────────
export async function getUserContributions(userId) {
  const priceQuery = query(
    collection(db, COLLECTIONS.PRICE_SUBMISSIONS),
    where("userId", "==", userId)
  );
  const plantingQuery = query(
    collection(db, COLLECTIONS.PLANTING_INTENTIONS),
    where("userId", "==", userId)
  );

  const [priceSnap, plantingSnap] = await Promise.all([
    getDocs(priceQuery),
    getDocs(plantingQuery),
  ]);

  const priceSubmissions = priceSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const validated = priceSubmissions.filter((s) => s.validated).length;

  return {
    totalSubmissions: priceSnap.size + plantingSnap.size,
    pricesSubmitted: priceSnap.size,
    plantingReports: plantingSnap.size,
    validatedPrices: validated,
  };
}

// ─── PDF Export (via print-friendly popup) ───────────────────────
export function exportPDF(sortedRows, highlights, t, userName, districtFilter) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  const timeStr = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const exporterName = userName || "Anonymous";

  const fmtPct = (v) => v != null ? (v > 0 ? "+" : "") + v + "%" : "\u2014";
  const pctColor = (v) => (v ?? 0) >= 0 ? "#16a34a" : "#dc2626";
  const statusColors = { rising: "#16a34a", falling: "#dc2626", stable: "#6b7280" };
  const signalColors = {
    STRONG_BUY: "#15803d", BUY: "#16a34a", HOLD: "#6b7280",
    AVOID: "#ea580c", STRONG_AVOID: "#dc2626",
  };
  const sig = (label) => t(`signals.${label}`, { defaultValue: label.replace("_", " ") });
  const dir = (status) => t(`priceStatus.${status}`, { defaultValue: status });
  const dirArrow = (status) => PRICE_DIRECTION[status]?.arrow || PRICE_DIRECTION.stable.arrow;

  // Location context
  const locationLabel = districtFilter && districtFilter !== "ALL"
    ? districtFilter + " District"
    : "All Districts : Nationwide";

  // ─── Table rows ─────────────────────
  const tableRows = sortedRows.map((r, i) => `
    <tr style="background:${i % 2 === 0 ? "#fff" : "#f9fafb"}">
      <td class="c" style="font-weight:600">${r.crop}</td>
      <td class="c" style="color:#6b7280;font-size:10px">${r.category || "\u2014"}</td>
      <td class="c r">${r.last != null ? `${r.last.toLocaleString()} RWF / ${r.priceUnit || "unit"}` : "\u2014"}</td>
      <td class="c r">${r.low != null ? `${r.low.toLocaleString()} RWF / ${r.priceUnit || "unit"}` : "\u2014"}</td>
      <td class="c r">${r.high != null ? `${r.high.toLocaleString()} RWF / ${r.priceUnit || "unit"}` : "\u2014"}</td>
      <td class="c r" style="color:${pctColor(r.change7)}">${fmtPct(r.change7)}</td>
      <td class="c r" style="color:${pctColor(r.change30)}">${fmtPct(r.change30)}</td>
      <td class="c r" style="color:${pctColor(r.change90)}">${fmtPct(r.change90)}</td>
      <td class="c ctr" style="color:${statusColors[r.priceStatus] || "#6b7280"};font-weight:700">${dirArrow(r.priceStatus)} ${dir(r.priceStatus)}</td>
      <td class="c r">${r.vol30 || 0}</td>
      <td class="c r">${r.volumeTrend30 != null ? fmtPct(r.volumeTrend30) : "\u2014"}</td>
      <td class="c ctr" style="color:${signalColors[r.signal] || "#6b7280"};font-weight:600">${sig(r.signal)}</td>
      <td class="c r">${r.proj30 != null ? r.proj30.toLocaleString() : "\u2014"}</td>
      <td class="c r">${r.proj90 != null ? r.proj90.toLocaleString() : "\u2014"}</td>
      <td class="c r">${r.proj180 != null ? r.proj180.toLocaleString() : "\u2014"}</td>
    </tr>`).join("");

  // ─── Highlights section ─────────────
  const hl = highlights || {};
  const highlightRow = (items, label, color) => {
    if (!items || items.length === 0) return "";
    return `<div style="margin-bottom:12px">
      <div style="font-weight:600;font-size:12px;color:${color};text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px">${label}</div>
      <div style="display:flex;gap:10px;flex-wrap:wrap">
        ${items.map(r => `<span style="padding:3px 10px;border-radius:6px;font-size:12px;background:${color}10;color:${color};border:1px solid ${color}30">${r.crop} ${fmtPct(r.change30)}</span>`).join("")}
      </div>
    </div>`;
  };

  const highlightsHtml = (hl.topGainers?.length || hl.topLosers?.length)
    ? `<div style="margin:24px 0;padding:16px;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb">
        <h3 style="margin:0 0 12px;color:#1f2937;font-size:14px">Market Highlights</h3>
        ${highlightRow(hl.topGainers, "Top Gainers (30d)", "#16a34a")}
        ${highlightRow(hl.topLosers, "Top Decliners (30d)", "#dc2626")}
        ${highlightRow(hl.oversupply, "Rising Supply", "#2563eb")}
        ${highlightRow(hl.undersupply, "Declining Supply", "#ea580c")}
      </div>`
    : "";

  // ─── Summary stats ─────────────────
  const totalCrops = sortedRows.length;
  const avgChange = sortedRows.filter(r => r.change30 != null);
  const avgMonth = avgChange.length > 0 ? (avgChange.reduce((s, r) => s + r.change30, 0) / avgChange.length).toFixed(1) : "N/A";
  const rising = sortedRows.filter(r => r.priceStatus === "rising").length;
  const falling = sortedRows.filter(r => r.priceStatus === "falling").length;
  const stable = sortedRows.filter(r => r.priceStatus === "stable").length;

  const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<title>Orrith - Cycle of Certainty \u2014 FarmPulse Market Report</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Inter, -apple-system, Helvetica, Arial, sans-serif; color: #1f2937; font-size: 13px; line-height: 1.5; }
  .page { max-width: 1100px; margin: 0 auto; padding: 24px 32px; }

  /* Header */
  .header { border-bottom: 3px solid #3b7d4e; padding-bottom: 16px; margin-bottom: 20px; }
  .brand { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
  .logo { width: 44px; height: 44px; background: #3b7d4e; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
  .logo span { color: #fff; font-size: 20px; font-weight: 800; }
  .brand-name { font-size: 22px; font-weight: 800; color: #3b7d4e; letter-spacing: -0.5px; }
  .brand-sub { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 1px; }
  .meta-row { display: flex; gap: 24px; flex-wrap: wrap; font-size: 12px; color: #6b7280; margin-top: 8px; }
  .meta-row span { display: inline-flex; align-items: center; gap: 4px; }

  /* Sections */
  h2 { color: #3b7d4e; font-size: 15px; font-weight: 700; margin: 20px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #e5e7eb; }
  .mission { background: linear-gradient(135deg, #f0fdf4, #ecfdf5); border: 1px solid #bbf7d0; border-radius: 8px; padding: 14px 16px; margin: 16px 0; font-size: 12px; color: #166534; line-height: 1.6; }
  .mission strong { color: #15803d; }

  /* Summary cards */
  .summary { display: flex; gap: 12px; margin: 16px 0; }
  .stat-card { flex: 1; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 14px; text-align: center; }
  .stat-card .val { font-size: 20px; font-weight: 700; color: #1f2937; }
  .stat-card .lbl { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }

  /* Table */
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 8px; }
  th { background: #f3f4f6; text-align: left; padding: 7px 8px; border-bottom: 2px solid #d1d5db; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; color: #374151; font-weight: 700; }
  th.r { text-align: right; }
  th.ctr { text-align: center; }
  .c { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; }
  .r { text-align: right; }
  .ctr { text-align: center; }

  /* Footer */
  .disclaimer { margin-top: 28px; padding: 14px 16px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; font-size: 11px; color: #92400e; line-height: 1.6; }
  .disclaimer strong { color: #78350f; }
  .cta { margin-top: 16px; padding: 14px 16px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; text-align: center; font-size: 12px; color: #166534; line-height: 1.6; }
  .cta strong { color: #15803d; }
  .doc-footer { margin-top: 24px; padding-top: 12px; border-top: 2px solid #3b7d4e; display: flex; justify-content: space-between; font-size: 10px; color: #9ca3af; }

  /* Legend */
  .legend { display: flex; gap: 16px; flex-wrap: wrap; margin: 10px 0 4px; font-size: 10px; color: #6b7280; }
  .legend span { display: inline-flex; align-items: center; gap: 3px; }
  .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }

  @media print {
    body { padding: 0; }
    .page { padding: 12px 16px; }
    .no-print { display: none; }
  }
</style>
</head><body>
<div class="page">

  <!-- ═══ HEADER ═══ -->
  <div class="header">
    <div class="brand">
      <div class="logo"><span>FN</span></div>
      <div>
        <div class="brand-name">Orrith - Cycle of Certainty</div>
        <div class="brand-sub">FarmPulse Market Intelligence Report</div>
      </div>
    </div>
    <div class="meta-row">
      <span><strong>Date:</strong> ${dateStr}</span>
      <span><strong>Time:</strong> ${timeStr}</span>
      <span><strong>Exported by:</strong> ${exporterName}</span>
      <span><strong>Location:</strong> ${locationLabel}</span>
      <span><strong>Crops tracked:</strong> ${totalCrops}</span>
    </div>
  </div>

  <!-- ═══ MISSION ═══ -->
  <div class="mission">
    Orrith - Cycle of Certainty is a community-driven platform dedicated to reducing food waste,
    strengthening farmer economies, supporting local communities, and fighting hunger.
    <br/><br/>
    FarmPulse is Orrith - Cycle of Certainty's crowdsourced market intelligence system. It aggregates
    real-time price data contributed by farmers, traders, and community members to generate actionable insights,
    trend analysis, and price forecasts empowering smarter agricultural decisions for everyone.
  </div>

  <!-- ═══ MARKET SUMMARY ═══ -->
  <h2>${t("export.priceSection")}</h2>

  <div class="summary">
    <div class="stat-card"><div class="val">${totalCrops}</div><div class="lbl">Crops Tracked</div></div>
    <div class="stat-card"><div class="val" style="color:${avgMonth !== "N/A" && parseFloat(avgMonth) >= 0 ? "#16a34a" : "#dc2626"}">${avgMonth !== "N/A" ? (parseFloat(avgMonth) > 0 ? "+" : "") + avgMonth + "%" : "N/A"}</div><div class="lbl">Avg. Monthly Change</div></div>
    <div class="stat-card"><div class="val" style="color:#16a34a">${rising}</div><div class="lbl">Rising</div></div>
    <div class="stat-card"><div class="val" style="color:#dc2626">${falling}</div><div class="lbl">Falling</div></div>
    <div class="stat-card"><div class="val" style="color:#6b7280">${stable}</div><div class="lbl">Stable</div></div>
  </div>

  <!-- Signal legend -->
  <div class="legend">
    <span><span class="dot" style="background:#15803d"></span> ${sig("STRONG_BUY")}</span>
    <span><span class="dot" style="background:#16a34a"></span> ${sig("BUY")}</span>
    <span><span class="dot" style="background:#6b7280"></span> ${sig("HOLD")}</span>
    <span><span class="dot" style="background:#ea580c"></span> ${sig("AVOID")}</span>
    <span><span class="dot" style="background:#dc2626"></span> ${sig("STRONG_AVOID")}</span>
  </div>

  <!-- ═══ FULL DATA TABLE ═══ -->
  <table>
    <thead><tr>
      <th>${t("table.crop")}</th>
      <th>${t("table.category", { defaultValue: "Category" })}</th>
      <th class="r">${t("table.lastPrice")}</th>
      <th class="r">${t("table.low")}</th>
      <th class="r">${t("table.high")}</th>
      <th class="r">${t("table.weekChange")}</th>
      <th class="r">${t("table.monthChange")}</th>
      <th class="r">${t("table.quarterChange")}</th>
      <th class="ctr">${t("table.direction")}</th>
      <th class="r">${t("table.activity")}</th>
      <th class="r">${t("table.supplyShift")}</th>
      <th class="ctr">${t("table.outlook")}</th>
      <th class="r">${t("table.forecast1m")}</th>
      <th class="r">${t("table.forecast3m", { defaultValue: "3M Forecast" })}</th>
      <th class="r">${t("table.forecast6m", { defaultValue: "6M Forecast" })}</th>
    </tr></thead>
    <tbody>${tableRows}</tbody>
  </table>

  ${highlightsHtml}

  <!-- ═══ DATA DISCLAIMER ═══ -->
  <div class="disclaimer">
    <strong>Data Notice:</strong> Orrith - Cycle of Certainty uses data contributed by community users through listed prices and submitted
    market prices. While we work hard to ensure accuracy, this data is crowdsourced and may not reflect exact
    market conditions at every location. If you find any information that appears inaccurate, we encourage you
    to provide the actual data by contributing through Orrith - Cycle of Certainty. Your participation helps improve accuracy for everyone.
  </div>

  <!-- ═══ CALL TO ACTION ═══ -->
  <div class="cta">
    <strong>Join Orrith - Cycle of Certainty today!</strong> Help reduce food waste, increase farmer income, support communities,
    and fight hunger. By contributing your local market prices, you become part of a growing movement
    making agriculture smarter &amp; more transparent across Rwanda.
    <br/>Visit <strong>orrith.app</strong> to get started.
  </div>

  <!-- ═══ FOOTER ═══ -->
  <div class="doc-footer">
    <span>Orrith - Cycle of Certainty • FarmPulse Market Intelligence &copy; ${now.getFullYear()}</span>
    <span>Report generated ${dateStr} at ${timeStr} by ${exporterName}</span>
  </div>

</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;

  printWindow.document.write(html);
  printWindow.document.close();
}
